/**
 * api/vendor.ts — vendor API module
 *
 * Covers two sets of endpoints:
 *   1. Public accept-invite flow (no auth required)
 *   2. Authenticated vendor dashboard endpoints (JWT required)
 */

import { apiFetch, resolveApiUrl, TOKEN_KEY, getToken, setAccessToken, logoutSession } from './client'

export const VENDOR_TOKEN_KEY = 'vendor_token'

export interface VendorLoginResult {
  token:  string
  vendor: { id: number; name: string; email: string; user_role: string }
}

/** POST /api/vendor/login — public, no JWT required */
export async function vendorLogin(email: string, password: string): Promise<VendorLoginResult> {
  const res = await fetch(resolveApiUrl('/api/vendor/login'), {
    method:  'POST',
    headers: { 'Content-Type': 'application/json' },
    body:    JSON.stringify({ email, password }),
  })

  if (res.status === 401) {
    throw new Error('Invalid email or password')
  }

  if (!res.ok) {
    const err = await res.json().catch(() => ({}))
    throw new Error((err as { error?: string }).error ?? `HTTP ${res.status}`)
  }

  return res.json() as Promise<VendorLoginResult>
}

/** POST /api/vendor/forgot-password — always ok; email only for vendor/sub */
export async function vendorForgotPassword(email: string): Promise<void> {
  const res = await fetch(resolveApiUrl('/api/vendor/forgot-password'), {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ email }),
  })
  if (!res.ok) {
    const err = await res.json().catch(() => ({}))
    throw new Error((err as { error?: string }).error ?? `HTTP ${res.status}`)
  }
}

/** POST /api/vendor/reset-password — returns session; navigate to /vendor/dashboard */
export async function vendorResetPassword(token: string, password: string): Promise<VendorLoginResult> {
  const res = await fetch(resolveApiUrl('/api/vendor/reset-password'), {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ token, password }),
  })
  if (!res.ok) {
    const err = await res.json().catch(() => ({}))
    throw new Error((err as { error?: string }).error ?? `HTTP ${res.status}`)
  }
  return res.json() as Promise<VendorLoginResult>
}

/** Persist vendor session tokens and return keys written. */
export function storeVendorSession(token: string): void {
  localStorage.setItem(VENDOR_TOKEN_KEY, token)
  localStorage.setItem(TOKEN_KEY, token)
}

/** Clear vendor + shared access tokens (localStorage). Pair with logoutSession() for cookie. */
export function clearVendorSession(): void {
  try {
    localStorage.removeItem(VENDOR_TOKEN_KEY)
    sessionStorage.removeItem(VENDOR_TOKEN_KEY)
  } catch { /* private browsing */ }
  setAccessToken(null)
}

/**
 * Full vendor sign-out: revoke refresh cookie, clear tokens, hard-navigate to login.
 * Hard replace so browser Back cannot restore an authed vendor page.
 */
export async function vendorLogout(opts?: { loggedOutNote?: boolean }): Promise<void> {
  clearVendorSession()
  try {
    await logoutSession()
  } catch { /* best effort — tokens already cleared */ }
  clearVendorSession() // again after logoutSession in case refresh raced
  const q = opts?.loggedOutNote === false ? '' : '?loggedOut=1'
  window.location.replace(`/vendor/login${q}`)
}


// ═══════════════════════════════════════════════════════════════════════════
// ACCEPT-INVITE FLOW (public, no JWT required)
// ═══════════════════════════════════════════════════════════════════════════

export interface VendorInviteData {
  token:                    string
  project_id:               number
  project_name:             string
  contractor_name:          string
  contractor_company_name:  string | null
  contractor_company_state: string | null
  vendor_company_name:      string
  vendor_contact_name:      string
  vendor_email:             string
  vendor_trade:             string | null
  vendor_phone:             string | null
  status:                   'invited' | 'accepted' | 'expired'
}

export interface AcceptInviteResult {
  token:        string
  project_id:   number
  project_name: string
}

export async function fetchInviteByToken(token: string): Promise<VendorInviteData> {
  const res = await apiFetch<{ data: VendorInviteData; error: string | null }>(
    'GET',
    `/api/vendor/accept-invite/${encodeURIComponent(token)}`,
  )
  return res.data
}

export async function acceptInvite(token: string): Promise<AcceptInviteResult> {
  const res = await apiFetch<{ data: AcceptInviteResult; error: string | null }>(
    'POST',
    `/api/vendor/accept-invite/${encodeURIComponent(token)}`,
  )
  return res.data
}

export async function acceptInviteByCode(code: string): Promise<AcceptInviteResult> {
  const res = await apiFetch<{ data: AcceptInviteResult; error: string | null }>(
    'POST',
    '/api/vendor/accept-invite-code',
    { code },
  )
  return res.data
}

// ═══════════════════════════════════════════════════════════════════════════
// AUTHENTICATED VENDOR DASHBOARD ENDPOINTS
// ═══════════════════════════════════════════════════════════════════════════

// ── My Projects ────────────────────────────────────────────────────────────

export interface VendorProject {
  id:               number
  name:             string
  address:          string | null
  status:           string
  vendor_status:    'invited' | 'active' | string
  invited_at:       string
  contractor_name:  string
  contractor_company_name?: string | null
  contractor_has_logo?: boolean
  contractor_email: string
}

/** Authenticated <img> src for the contractor's logo on a vendor project. */
export function contractorLogoUrl(projectId: number): string {
  const token = getToken() ?? ''
  return resolveApiUrl(`/api/vendor/projects/${projectId}/contractor-logo?token=${encodeURIComponent(token)}`)
}

export async function fetchMyProjects(): Promise<VendorProject[]> {
  const res = await apiFetch<{ data: VendorProject[]; error: string | null }>(
    'GET',
    '/api/vendor/my-projects',
  )
  return res.data ?? []
}

// ── My Documents ───────────────────────────────────────────────────────────

export interface VendorDocument {
  id:           number
  project_id:   number
  project_name: string
  original_name: string
  doc_type:     string
  status:       'pending' | 'pending_review' | 'approved' | 'rejected' | 'changes_requested' | string
  file_size:    number
  uploaded_at:  string
  paid_at?:     string | null
  paid_amount?: string | null
  uploaded_by_role?: string | null
  insurance_expires?: string | null
  filed_by_company?: string | null
}

export async function fetchMyDocuments(): Promise<VendorDocument[]> {
  const res = await apiFetch<{ data: VendorDocument[]; error: string | null }>(
    'GET',
    '/api/vendor/documents',
  )
  return res.data ?? []
}

// ── Trust Score ────────────────────────────────────────────────────────────

export interface TrustScoreData {
  id:               number | null
  computedScore:    number
  effectiveScore:   number
  overrideScore:    number | null
  isOverridden:     boolean
  tier:             string
  tierLabel:        string
  isVisibleToVendor: boolean
  lastEventAt:      string | null
}

export async function fetchVendorTrustScore(vendorUserId: number): Promise<TrustScoreData> {
  const res = await apiFetch<{ data: TrustScoreData; error: string | null }>(
    'GET',
    `/api/trust-score/${vendorUserId}`,
  )
  return res.data
}

// ── Upload Document ────────────────────────────────────────────────────────

export async function uploadVendorDocument(
  projectId: number,
  file: File,
  docType: string,
  opts?: { insuranceExpires?: string | null },
): Promise<VendorDocument> {
  const token = (await import('./client')).getToken()
  const form = new FormData()
  form.append('file', file)
  form.append('doc_type', docType)
  if (opts?.insuranceExpires) {
    form.append('insurance_expires', opts.insuranceExpires)
  }

  const res = await fetch(
    resolveApiUrl(`/api/vendor/projects/${projectId}/documents`),
    {
      method: 'POST',
      headers: token ? { Authorization: `Bearer ${token}` } : {},
      body: form,
    },
  )

  if (!res.ok) {
    const err = await res.json().catch(() => ({}))
    throw new Error((err as { error?: string }).error ?? `HTTP ${res.status}`)
  }

  const json = await res.json()
  return json.data
}

// ── Profile ────────────────────────────────────────────────────────────────

export interface VendorProfileData {
  company_name:  string | null
  contact_name:  string | null
  email:         string
  phone:         string | null
  trade:         string | null
}

export async function fetchVendorProfile(): Promise<VendorProfileData> {
  const res = await apiFetch<{ data: VendorProfileData; error: string | null }>(
    'GET',
    '/api/vendor/me',
  )
  return res.data
}

export async function updateVendorProfile(
  patch: Partial<Pick<VendorProfileData, 'phone' | 'trade'>>,
): Promise<VendorProfileData> {
  const res = await apiFetch<{ data: VendorProfileData; error: string | null }>(
    'PATCH',
    '/api/vendor/me',
    patch,
  )
  return res.data
}

// ── Project Detail (fires event on server) ────────────────────────────────

export async function fetchVendorProjectDetail(id: number): Promise<VendorProject> {
  const res = await apiFetch<{ data: VendorProject; error: string | null }>(
    'GET',
    `/api/vendor/projects/${id}`,
  )
  return res.data
}

// ── Notifications ──────────────────────────────────────────────────────────

export interface VendorNotification {
  id:           number
  type:         string
  title:        string
  body:         string | null
  read:         boolean
  upload_id:    number | null
  project_id:   number | null
  project_name: string | null
  created_at:   string
}

export async function fetchVendorNotifications(): Promise<VendorNotification[]> {
  const res = await apiFetch<{ data: VendorNotification[]; error: string | null }>(
    'GET',
    '/api/vendor/notifications',
  )
  return res.data ?? []
}
// v: fix-vendor-my-projects
